import "./ManagePricing.css";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  clearUpdateStatus,
  fetchPricing,
  updatePricing,
} from "./pricingSlice";

const formatSessionName = (name) => {
  if (!name) {
    return "Untitled option";
  }

  const normalized = name.replace(/[-_]+/g, " ");
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

function ManagePricing() {
  const dispatch = useDispatch();
  const pricing = useSelector((state) => state.pricing.items);
  const status = useSelector((state) => state.pricing.status);
  const fetchError = useSelector((state) => state.pricing.error);
  const updateStatus = useSelector((state) => state.pricing.updateStatus);
  const [editedPricing, setEditedPricing] = useState({});
  const [rowErrors, setRowErrors] = useState({});

  useEffect(() => {
    if (status === "idle") {
      dispatch(fetchPricing());
    }
  }, [dispatch, status]);

  useEffect(() => {
    const initialValues = pricing.reduce((accumulator, item) => {
      accumulator[item.id] = item.pricePerHead ?? "";
      return accumulator;
    }, {});

    setEditedPricing(initialValues);
  }, [pricing]);

  const pricingWithFallbacks = useMemo(
    () =>
      pricing.map((item) => ({
        ...item,
        label: item.label || formatSessionName(item.id),
      })),
    [pricing]
  );

  const handlePriceChange = (id, value) => {
    setEditedPricing((previous) => ({
      ...previous,
      [id]: value,
    }));

    if (rowErrors[id]) {
      setRowErrors((previous) => {
        const nextErrors = { ...previous };
        delete nextErrors[id];
        return nextErrors;
      });
    }

    if (updateStatus[id]) {
      dispatch(clearUpdateStatus(id));
    }
  };

  const handleSubmit = async (event, pricingId) => {
    event.preventDefault();
    const rawValue = editedPricing[pricingId];
    const numericValue = Number(rawValue);

    if (rawValue === "" || Number.isNaN(numericValue) || numericValue < 0) {
      setRowErrors((previous) => ({
        ...previous,
        [pricingId]: "Enter a price of zero or higher.",
      }));
      return;
    }

    try {
      await dispatch(
        updatePricing({ id: pricingId, pricePerHead: numericValue })
      ).unwrap();
    } catch (error) {
      setRowErrors((previous) => ({
        ...previous,
        [pricingId]: "We couldn't save that price. Try again.",
      }));
    }
  };

  const renderContent = () => {
    if (status === "loading") {
      return <p className="manage-pricing__status">Loading pricing…</p>;
    }

    if (status === "failed") {
      return (
        <div className="manage-pricing__status manage-pricing__status--error">
          <p>We couldn't load your pricing right now.</p>
          <button
            type="button"
            className="manage-pricing__retry"
            onClick={() => dispatch(fetchPricing())}
          >
            Try again
          </button>
          {fetchError && <p className="manage-pricing__hint">{fetchError}</p>}
        </div>
      );
    }

    if (!pricingWithFallbacks.length) {
      return (
        <p className="manage-pricing__status">
          No pricing options are set up yet. Add them in Firebase to see them
          here.
        </p>
      );
    }

    return (
      <ul className="pricing-cards" aria-live="polite">
        {pricingWithFallbacks.map((item) => {
          const fieldError = rowErrors[item.id];
          const statusForRow = updateStatus[item.id]?.status;
          const isSaving = statusForRow === "pending";
          const isSuccess = statusForRow === "succeeded";
          const statusMessage = updateStatus[item.id]?.error;

          return (
            <li key={item.id} className="pricing-card">
              <form onSubmit={(event) => handleSubmit(event, item.id)}>
                <div className="pricing-card__header">
                  <h2 className="pricing-card__title">{item.label}</h2>
                  <span className="pricing-card__subtitle">{item.id}</span>
                </div>

                <label className="pricing-card__label" htmlFor={`price-${item.id}`}>
                  Price per guest
                </label>
                <div className="pricing-card__controls">
                  <span className="pricing-card__currency" aria-hidden="true">
                    R
                  </span>
                  <input
                    id={`price-${item.id}`}
                    type="number"
                    min="0"
                    step="0.01"
                    inputMode="decimal"
                    className={`pricing-card__input${
                      fieldError ? " pricing-card__input--error" : ""
                    }`}
                    value={editedPricing[item.id] ?? ""}
                    onChange={(event) =>
                      handlePriceChange(item.id, event.target.value)
                    }
                    aria-describedby={
                      fieldError || statusForRow
                        ? `price-feedback-${item.id}`
                        : undefined
                    }
                  />
                  <button
                    type="submit"
                    className="pricing-card__save"
                    disabled={isSaving}
                  >
                    {isSaving ? "Saving…" : "Save"}
                  </button>
                </div>
                {(fieldError || statusForRow) && (
                  <p
                    id={`price-feedback-${item.id}`}
                    className={`pricing-card__feedback${
                      fieldError
                        ? " pricing-card__feedback--error"
                        : isSuccess
                        ? " pricing-card__feedback--success"
                        : ""
                    }`}
                    role={fieldError ? "alert" : "status"}
                  >
                    {fieldError
                      ? fieldError
                      : isSuccess
                      ? "Saved"
                      : statusMessage || "Unable to save changes."}
                  </p>
                )}
              </form>
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <section className="manage-pricing">
      <header className="manage-pricing__header">
        <h1>Manage pricing</h1>
        <p>
          Keep your experiences up to date by adjusting the price guests pay
          for each session. Changes are applied immediately.
        </p>
      </header>
      {renderContent()}
    </section>
  );
}

export default ManagePricing;
